/**
 * Document Filters Hook
 *
 * Maps the generic Filter component's activeFilters state
 * to the UseDocumentsFilters shape expected by useDocuments().
 */

import { useMemo } from "react";
import { useFilter } from "@/components/ui";

interface DocumentFiltersInput {
  search?: string;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
}

interface DocumentFilters {
  search?: string;
  tagIds?: string[];
  fileType?: string;
  statuses?: string[];
  statusOperator?: "is" | "is_not";
  categoryIds?: string[];
  categoryOperator?: "is" | "is_not";
  sortBy?: string;
  sortOrder?: "asc" | "desc";
}

/**
 * Maps activeFilters from the Filter context into the query params
 * expected by the listDocuments Convex query.
 *
 * Category mapping:
 *   - "status"   → filters.statuses (all selected values) + statusOperator
 *   - "file_type" → filters.fileType (first selected value)
 *   - "tags"     → filters.tagIds (all selected values)
 *   - "category" → filters.categoryIds (all selected values) + categoryOperator
 */
export function useDocumentFilters(
  input?: DocumentFiltersInput,
): DocumentFilters {
  const { filters } = useFilter();

  return useMemo(() => {
    const result: DocumentFilters = {};

    if (input?.search) {
      result.search = input.search;
    }
    if (input?.sortBy) {
      result.sortBy = input.sortBy;
      result.sortOrder = input.sortOrder;
    }

    for (const filter of filters) {
      if (filter.valueIds.length === 0) continue;

      switch (filter.categoryId) {
        case "status":
          result.statuses = filter.valueIds;
          result.statusOperator =
            filter.operator === "is_not" ? "is_not" : "is";
          break;
        case "file_type":
          // Skip negation for file_type — not supported
          if (filter.operator === "is_not") continue;
          result.fileType = filter.valueIds[0];
          break;
        case "tags":
          // Skip negation for tags — not supported
          if (filter.operator === "includes_none") continue;
          result.tagIds = filter.valueIds;
          break;
        case "category":
          result.categoryIds = filter.valueIds;
          result.categoryOperator =
            filter.operator === "is_not" ? "is_not" : "is";
          break;
      }
    }

    return result;
  }, [filters, input?.search, input?.sortBy, input?.sortOrder]);
}
